import { useState, useCallback } from "react";
import { PendingChange } from "./types";

export type UsePendingChangesReturn = ReturnType<typeof usePendingChanges>;

export function usePendingChanges() {
  const [changes, setChanges] = useState<PendingChange[]>([]);

  const addChange = useCallback((change: PendingChange) => {
    setChanges((prev) => {
      const idx = prev.findIndex((c) => c.key === change.key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = change;
        return next;
      }
      return [...prev, change];
    });
  }, []);

  const removeChange = useCallback((key: string) => {
    setChanges((prev) => prev.filter((c) => c.key !== key));
  }, []);

  const clearAll = useCallback(() => setChanges([]), []);

  const addMultiple = useCallback((newChanges: PendingChange[]) => {
    setChanges((prev) => {
      const map = new Map(prev.map((c) => [c.key, c]));
      newChanges.forEach((c) => map.set(c.key, c));
      return Array.from(map.values());
    });
  }, []);

  const toBulkPayload = useCallback(() => {
    return changes.map((c) => ({
      action: c.action,
      entityType: c.entityType,
      id: c.id,
      data: c.data,
    }));
  }, [changes]);

  return {
    changes,
    addChange,
    removeChange,
    clearAll,
    addMultiple,
    toBulkPayload,
    hasChanges: changes.length > 0,
  };
}
